import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiCheck, FiChevronDown, FiPlus, FiX } from 'react-icons/fi';

// Building blocks of the new platform (Apple-like style). Bootstrap is still loaded
// globally: spacing uses arbitrary values and every button sets its own background.

export const CARD = 'rounded-3xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]';

export const INPUT =
  'w-full rounded-xl border-[1px] border-solid border-black/10 bg-white px-[14px] text-[15px] text-ink outline-none transition placeholder:text-black/30 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 disabled:bg-black/[0.03] disabled:text-black/40';

const BUTTON_BASE =
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border-0 font-medium no-underline transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50';

export const BUTTON = {
  primary: `${BUTTON_BASE} bg-brand-600 text-white hover:bg-brand-700 hover:text-white focus-visible:ring-brand-600/25`,
  secondary: `${BUTTON_BASE} bg-black/[0.05] text-ink hover:bg-black/[0.09] hover:text-ink focus-visible:ring-black/10`,
  danger: `${BUTTON_BASE} bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 focus-visible:ring-red-500/20`,
  dangerSolid: `${BUTTON_BASE} bg-red-600 text-white hover:bg-red-700 hover:text-white focus-visible:ring-red-500/25`,
  ghost: `${BUTTON_BASE} bg-[transparent] text-brand-600 hover:bg-brand-50 hover:text-brand-700 focus-visible:ring-brand-600/20`,
};
export const SIZE = { sm: 'h-9 px-[14px] text-[13.5px]', md: 'h-11 px-[20px] text-[14px]' };

const TONES = {
  neutral: 'bg-black/[0.05] text-black/60',
  brand: 'bg-brand-50 text-brand-700',
  green: 'bg-emerald-50 text-emerald-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
  violet: 'bg-violet-50 text-violet-700',
  slate: 'bg-slate-100 text-slate-600',
  dark: 'bg-ink text-white',
};

export const Pill = ({ tone = 'neutral', children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-[10px] py-1 text-[12px] font-medium ${TONES[tone] || TONES.neutral} ${className}`}>
    {children}
  </span>
);

export const PageHeader = ({ back, eyebrow, title, subtitle, actions }) => (
  <header className="flex flex-col gap-[16px] md:flex-row md:items-end md:justify-between">
    <div className="min-w-0">
      {back && (
        <Link
          to={back.to}
          className="mb-[14px] inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-600 no-underline hover:text-brand-700"
        >
          <FiArrowLeft size={15} aria-hidden="true" />
          {back.label}
        </Link>
      )}
      {eyebrow && <div className="mb-2 text-[13px] font-medium text-black/45">{eyebrow}</div>}
      <h1 className="m-0 break-words text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">{title}</h1>
      {subtitle && <div className="mt-2 text-[15px] text-black/50">{subtitle}</div>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 print:hidden">{actions}</div>}
  </header>
);

export const Panel = ({ title, description, action, children, className = '', id }) => (
  <section id={id} className={`${CARD} p-[20px] sm:p-6 ${className}`}>
    {(title || action) && (
      <div className="mb-[16px] flex items-start justify-between gap-[12px]">
        <div className="min-w-0">
          {title && <h2 className="m-0 text-[16px] font-semibold tracking-tight text-ink">{title}</h2>}
          {description && <p className="mb-0 mt-1 text-[13.5px] leading-snug text-black/45">{description}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Field = ({ id, label, hint, error, required, wide = false, children }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-black/60">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {children}
    {error ? (
      <p className="mb-0 mt-1.5 text-[12.5px] text-red-600" role="alert">
        {error}
      </p>
    ) : (
      hint && <p className="mb-0 mt-1.5 text-[12.5px] text-black/40">{hint}</p>
    )}
  </div>
);

export const TextInput = ({ className = '', invalid = false, ...props }) => (
  <input {...props} aria-invalid={invalid || undefined} className={`${INPUT} h-11 ${invalid ? 'border-red-400' : ''} ${className}`} />
);

export const TextArea = ({ value = '', className = '', ...props }) => (
  <textarea
    {...props}
    value={value}
    rows={Math.min(16, Math.max(3, String(value).split('\n').length + 1))}
    className={`${INPUT} resize-y py-[10px] leading-relaxed ${className}`}
  />
);

// options: ['A', 'B'] or [{ value, label }]; groups: [{ label, options }]
const normalize = (options = []) => options.map((option) => (typeof option === 'string' ? { value: option, label: option } : option));

export const Select = ({ options, groups, placeholder = 'Choisir…', className = '', ...props }) => (
  <div className="relative">
    <select {...props} className={`${INPUT} h-11 cursor-pointer appearance-none pr-[40px] ${className}`}>
      <option value="">{placeholder}</option>
      {groups
        ? groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {normalize(group.options).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))
        : normalize(options).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
    </select>
    <FiChevronDown size={17} aria-hidden="true" className="pointer-events-none absolute right-[14px] top-1/2 -translate-y-1/2 text-black/40" />
  </div>
);

// Single choice shown as a row of buttons ("Oui / Non", "Masculin / Féminin").
export const Segmented = ({ id, value, onChange, options, allowEmpty = true }) => (
  <div id={id} role="radiogroup" className="inline-flex flex-wrap gap-1 rounded-2xl bg-black/[0.04] p-1">
    {normalize(options).map((option) => {
      const active = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(active && allowEmpty ? '' : option.value)}
          className={`h-9 cursor-pointer whitespace-nowrap rounded-xl border-0 px-[14px] text-[14px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
            active ? 'bg-white text-ink shadow-[0_1px_3px_rgba(0,0,0,0.12)]' : 'bg-[transparent] text-black/55 hover:text-ink'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

// Several choices among a list, plus free entries.
export const MultiChips = ({ id, options = [], values = [], onChange, customPlaceholder = 'Autre…' }) => {
  const [custom, setCustom] = useState('');
  const extra = values.filter((value) => !options.includes(value));
  const toggle = (value) => onChange(values.includes(value) ? values.filter((item) => item !== value) : [...values, value]);
  const addCustom = () => {
    const value = custom.trim();
    if (value && !values.includes(value)) onChange([...values, value]);
    setCustom('');
  };
  return (
    <div id={id}>
      <div className="flex flex-wrap gap-1.5">
        {[...options, ...extra].map((option) => {
          const active = values.includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(option)}
              className={`inline-flex h-8 cursor-pointer items-center gap-1 rounded-full border-[1px] border-solid px-[12px] text-[13px] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
                active
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-black/10 bg-white text-black/65 hover:border-black/25 hover:text-ink'
              }`}
            >
              {active && <FiCheck size={13} aria-hidden="true" />}
              {option}
            </button>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={customPlaceholder}
          aria-label={customPlaceholder}
          className={`${INPUT} h-9 max-w-[320px] text-[14px]`}
        />
        <button type="button" onClick={addCustom} disabled={!custom.trim()} className={`${BUTTON.secondary} ${SIZE.sm}`}>
          <FiPlus size={14} aria-hidden="true" />
          Ajouter
        </button>
      </div>
    </div>
  );
};

export const Modal = ({ open, title, onClose, children, footer, size = 'md' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-[16px] backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`max-h-[90vh] w-full overflow-y-auto rounded-3xl bg-white p-6 shadow-[0_24px_64px_rgba(0,0,0,0.25)] sm:p-8 ${
          size === 'lg' ? 'max-w-[640px]' : 'max-w-[460px]'
        }`}
      >
        <div className="flex items-start justify-between gap-[12px]">
          <h2 className="m-0 text-[20px] font-semibold tracking-tight text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-black/[0.05] text-black/55 transition hover:bg-black/[0.09] hover:text-ink"
          >
            <FiX size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="mt-[16px]">{children}</div>
        {footer && <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{footer}</div>}
      </div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, children, action }) => (
  <div className="flex flex-col items-center px-[16px] py-12 text-center">
    {Icon && (
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black/[0.04] text-black/45">
        <Icon size={22} aria-hidden="true" />
      </span>
    )}
    <p className="mb-0 mt-[16px] text-[16px] font-semibold text-ink">{title}</p>
    {children && <p className="mb-0 mt-1 max-w-sm text-[14px] leading-relaxed text-black/50">{children}</p>}
    {action && <div className="mt-[20px]">{action}</div>}
  </div>
);

export const SkeletonRows = ({ rows = 4 }) => (
  <div className="flex flex-col gap-[16px]" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-[12px]">
        <span className="h-10 w-10 flex-none animate-pulse rounded-full bg-black/[0.06]" />
        <span className="flex-1">
          <span className="block h-3 w-1/2 animate-pulse rounded-full bg-black/[0.06]" />
          <span className="mt-2 block h-3 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
        </span>
      </div>
    ))}
  </div>
);

export const Notice = ({ tone = 'neutral', icon: Icon, children }) => {
  const tones = {
    neutral: 'bg-mist text-black/60',
    amber: 'bg-amber-50 text-amber-900 ring-1 ring-amber-200',
    red: 'bg-red-50 text-red-700 ring-1 ring-red-200',
    brand: 'bg-brand-50 text-brand-900 ring-1 ring-brand-100',
    green: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200',
  };
  return (
    <div className={`flex items-start gap-[10px] rounded-2xl px-[16px] py-[12px] text-[14px] leading-relaxed ${tones[tone]}`} role="status">
      {Icon && <Icon size={17} aria-hidden="true" className="mt-0.5 flex-none" />}
      <div className="min-w-0">{children}</div>
    </div>
  );
};
